import React, { useMemo, useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { AllocationModal, AllocationBarInfo } from './AllocationModal';

interface StandaloneAllocationModalProps {
  entityId: string;
  entityLabel: string;
  personId: string;
  onClose: () => void;
}

/**
 * Smart wrapper over AllocationModal for use outside PlanningGrid.
 * Computes planning rhythm periods and capacity from the person's teams,
 * then delegates to AllocationModal.
 */
export const StandaloneAllocationModal: React.FC<StandaloneAllocationModalProps> = ({
  entityId,
  entityLabel,
  personId,
  onClose,
}) => {
  const { database, applyCommand } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const { workspaceId } = useParams<{ workspaceId: string }>();

  // Resolve person's team rhythms
  const [rhythms, setRhythms] = useState<{ label: string; intervalDays: number; startDay: string }[]>([]);
  const [selectedRhythmIdx, setSelectedRhythmIdx] = useState(0);

  useEffect(() => {
    if (!database || !safeData) return;

    const person = database.people()[personId];
    if (!person) return;

    person.team_ids(safeData).then((teamIds: string[]) => {
      const teams = database.teams();
      const found: { label: string; intervalDays: number; startDay: string }[] = [];
      for (const tid of teamIds) {
        const team = teams[tid];
        if (!team) continue;
        const rhythm = team.planning_rhythm?.();
        if (rhythm?.interval_days) {
          found.push({
            label: team.name(),
            intervalDays: rhythm.interval_days,
            startDay: rhythm.start_day ?? 'Mon',
          });
        }
      }
      // Default weekly rhythm if none found
      if (found.length === 0) {
        found.push({ label: 'Weekly', intervalDays: 7, startDay: 'Mon' });
      }
      setRhythms(found);
    });
  }, [database, safeData, personId]);

  // Compute period from selected rhythm
  const { days, personCells, defaultStartIdx, defaultEndIdx } = useMemo(() => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const rhythm = rhythms[selectedRhythmIdx];
    const intervalDays = rhythm?.intervalDays ?? 7;

    // Find current period start: walk back from today to the rhythm start day
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const startDayIdx = dayNames.indexOf(rhythm?.startDay ?? 'Mon');
    const todayDayIdx = (today.getDay() + 6) % 7; // Convert JS Sunday=0 to Mon=0
    const daysBack = (todayDayIdx - startDayIdx + 7) % 7;
    const periodStart = new Date(today);
    periodStart.setDate(periodStart.getDate() - daysBack);

    // Build days for current period + next period (2 periods)
    const totalDays = intervalDays * 2;
    const daysArr: { date: string }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(periodStart);
      d.setDate(d.getDate() + i);
      daysArr.push({ date: d.toISOString().slice(0, 10) });
    }

    // Get person's availability to compute capacity per day
    // Use 10 tenths (full day) as default, weekends = 0
    const cells: Record<string, any> = {};
    let personAvailability: any[] = [];
    if (database) {
      const person = database.people()[personId];
      if (person) {
        personAvailability = person.availability?.() ?? [];
      }
    }

    // Get existing allocations for committed effort tracking
    const existingAllocations: any[] = database
      ? database.manual_allocations_for_person?.(personId) ?? []
      : [];

    for (let i = 0; i < daysArr.length; i++) {
      const dateStr = daysArr[i].date;
      const d = new Date(dateStr + 'T12:00:00');
      const dayOfWeek = d.getDay(); // 0=Sun, 6=Sat

      // Default capacity: 10 tenths on weekdays, 0 on weekends
      let capacity = (dayOfWeek === 0 || dayOfWeek === 6) ? 0 : 10;

      // Override with person-level availability if set
      for (const avail of personAvailability) {
        if (avail.after && dateStr < avail.after) continue;
        if (avail.before && dateStr >= avail.before) continue;
        const policy = avail.policy;
        if (policy?.capacity !== undefined) {
          capacity = policy.capacity;
        }
      }

      // Sum already-utilized effort on this day
      let utilized = 0;
      const committed: any[] = [];
      for (const pa of existingAllocations) {
        const alloc = pa.allocation;
        const epoch = alloc.when;
        let matches = false;
        if (epoch?.type === 'Day' && epoch.day === dateStr) {
          matches = true;
        } else if (epoch?.type === 'Days' && dateStr >= epoch.start_inclusive && dateStr <= epoch.end_inclusive) {
          matches = true;
        }
        if (matches) {
          utilized += alloc.time ?? 0;
          committed.push({ entity: { id: pa.entity }, effort: alloc.time ?? 0 });
        }
      }

      cells[i.toString()] = { capacity, utilized, committed };
    }

    // Find the index of today in the days array
    const todayIdx = daysArr.findIndex(d => d.date >= todayStr);
    const startIdx = Math.max(0, todayIdx);
    // End at the end of current rhythm period
    const endIdx = Math.min(daysArr.length - 1, startIdx + intervalDays - 1);

    return { days: daysArr, personCells: cells, defaultStartIdx: startIdx, defaultEndIdx: endIdx };
  }, [rhythms, selectedRhythmIdx, database, personId]);

  // Compute remaining effort for this task: estimate minus already-committed effort
  const remainingEffort = useMemo(() => {
    if (!database) return 10;
    try {
      const task = database.tasks()[entityId];
      if (!task) return 10;
      const estimates = task.estimates?.() ?? [];
      const totalEstimate = estimates.length > 0 ? (estimates[0] as any)?.value ?? 0 : 0;
      if (totalEstimate <= 0) return 10;

      // Sum already-committed effort for this entity across all persons
      let committedForEntity = 0;
      for (const cell of Object.values(personCells)) {
        for (const alloc of ((cell as any)?.committed ?? [])) {
          if ((alloc.entity as any)?.id === entityId) {
            committedForEntity += (alloc as any).effort ?? 0;
          }
        }
      }

      return Math.max(1, totalEstimate - committedForEntity);
    } catch {
      return 10;
    }
  }, [database, entityId, personCells]);

  const bar: AllocationBarInfo = useMemo(() => ({
    label: entityLabel,
    totalEffort: remainingEffort,
    startIdx: defaultStartIdx,
    endIdx: defaultEndIdx,
    alloc: { entity: { id: entityId } },
    personId,
  }), [entityLabel, entityId, personId, defaultStartIdx, defaultEndIdx, remainingEffort]);

  return (
    <div>
      {/* Rhythm selector (shown above the modal content if multiple rhythms) */}
      {rhythms.length > 1 && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-20 pointer-events-none">
          <div className="pointer-events-auto mb-2 flex gap-1.5 bg-cm-bg-deep border border-cm-border-subtle rounded-lg p-1.5 shadow-xl -translate-y-12">
            {rhythms.map((r, i) => (
              <button
                key={i}
                onClick={() => setSelectedRhythmIdx(i)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono transition-colors ${
                  i === selectedRhythmIdx
                    ? 'bg-cm-accent-blue/20 text-cm-accent-blue border border-cm-accent-blue/40'
                    : 'text-cm-text-muted border border-transparent hover:border-cm-border-mid'
                }`}
              >
                {r.label} ({r.intervalDays}d)
              </button>
            ))}
          </div>
        </div>
      )}
      <AllocationModal
        bar={bar}
        variant="suggestion"
        days={days}
        workspaceId={workspaceId ?? ''}
        database={database}
        personCells={personCells}
        applyCommand={applyCommand}
        onClose={onClose}
      />
    </div>
  );
};
